// libraries.
import { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFieldArrayReturn,
} from "react-hook-form";

// styles.
import { StyledRulesModalForm } from "../../styles";

// constants.
import { CONTEXT_VARIABLES, CONDITION_OPERATORS } from "../rulesFormConstants";

// types.
import { EditStepFormValues, SubConditionValues } from "./useEditStepForm";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  register: UseFormRegister<EditStepFormValues>;
  errors: FieldErrors<EditStepFormValues>;
  watch: UseFormWatch<EditStepFormValues>;
  setValue: UseFormSetValue<EditStepFormValues>;
  hasDayRange: boolean;
  hasPercentage: boolean;
  subConditionArray: UseFieldArrayReturn<EditStepFormValues, "subConditions">;
  emptySubCondition: SubConditionValues;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EditStepFields = ({
  register,
  errors,
  watch,
  setValue,
  hasDayRange,
  hasPercentage,
  subConditionArray,
  emptySubCondition,
}: Props) => {
  const [conditionOpen, setConditionOpen] = useState(false);
  const conditionMode = watch("conditionMode");
  const dayRangeFrom = watch("dayRangeFrom") ?? 0;

  return (
    <StyledRulesModalForm className="d-flex flex-column gap-3">

      {/* Nombre + Prioridad */}
      <Row className="g-3">
        <Col xs={9}>
          <Form.Group>
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Nombre del paso"
              {...register("name", { required: "Requerido" })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col xs={3}>
          <Form.Group>
            <Form.Label>Prioridad</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min={0}
              {...register("priority", {
                required: "Requerido",
                valueAsNumber: true,
                min: { value: 0, message: "≥ 0" },
              })}
              isInvalid={!!errors.priority}
            />
            <Form.Control.Feedback type="invalid">
              {errors.priority?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Descripción */}
      <Form.Group>
        <Form.Label>Descripción</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          placeholder="Describe el comportamiento de este paso"
          {...register("description", { required: "Requerido" })}
          isInvalid={!!errors.description}
        />
        <Form.Control.Feedback type="invalid">
          {errors.description?.message}
        </Form.Control.Feedback>
      </Form.Group>

      {/* ── Condición — desplegable ────────────────────────────────────────── */}
      <div style={{ border: "1px solid #d0c4f7", borderRadius: 6, overflow: "hidden" }}>
        <button
          type="button"
          onClick={() => setConditionOpen((o) => !o)}
          style={{
            width: "100%",
            background: "#f3f0ff",
            border: "none",
            borderBottom: conditionOpen ? "1px solid #d0c4f7" : "none",
            padding: "6px 12px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 13,
            fontWeight: 600,
            color: "#5c00b8",
          }}
          aria-expanded={conditionOpen}
        >
          <span>Condición</span>
          <span>{conditionOpen ? "▲" : "▼"}</span>
        </button>

        {conditionOpen && (
          <div className="p-2 d-flex flex-column gap-2">

            <Form.Group>
              <Form.Label style={{ fontSize: 12 }}>Tipo de condición</Form.Label>
              <Form.Select
                size="sm"
                {...register("conditionMode")}
                onChange={(e) => {
                  const mode = e.target.value as EditStepFormValues["conditionMode"];
                  setValue("conditionMode", mode);
                  if (mode !== "simple" && subConditionArray.fields.length === 0) {
                    subConditionArray.append(emptySubCondition);
                  }
                }}
              >
                <option value="simple">Simple</option>
                <option value="and">Compuesta — AND (todas deben cumplirse)</option>
                <option value="or">Compuesta — OR (al menos una debe cumplirse)</option>
              </Form.Select>
            </Form.Group>

            {conditionMode === "simple" && (
              <Row className="g-2">
                <Col xs={5}>
                  <Form.Select
                    size="sm"
                    {...register("conditionLeft", { required: "Requerido" })}
                    isInvalid={!!errors.conditionLeft}
                  >
                    <option value="">Variable...</option>
                    {CONTEXT_VARIABLES.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={4}>
                  <Form.Select size="sm" {...register("conditionType")}>
                    {CONDITION_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={3}>
                  <Form.Control
                    size="sm"
                    placeholder="Valor"
                    {...register("conditionRight", { required: "Requerido" })}
                    isInvalid={!!errors.conditionRight}
                  />
                </Col>
              </Row>
            )}

            {(conditionMode === "and" || conditionMode === "or") && (
              <div className="d-flex flex-column gap-2">
                {subConditionArray.fields.map((field, idx) => (
                  <Row key={field.id} className="g-2 align-items-center">
                    <Col xs={4}>
                      <Form.Select
                        size="sm"
                        {...register(`subConditions.${idx}.left`, { required: true })}
                      >
                        <option value="">Variable...</option>
                        {CONTEXT_VARIABLES.map((v) => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={4}>
                      <Form.Select size="sm" {...register(`subConditions.${idx}.type`)}>
                        {CONDITION_OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={3}>
                      <Form.Control
                        size="sm"
                        placeholder="Valor"
                        {...register(`subConditions.${idx}.right`, { required: true })}
                      />
                    </Col>
                    <Col xs={1} className="text-end">
                      {subConditionArray.fields.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-item"
                          onClick={() => subConditionArray.remove(idx)}
                        >
                          ✕
                        </button>
                      )}
                    </Col>
                  </Row>
                ))}
                <button
                  type="button"
                  className="btn-add-item"
                  onClick={() => subConditionArray.append(emptySubCondition)}
                >
                  + Agregar condición
                </button>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Rango de días — solo si la regla tiene condiciones dias_full */}
      {hasDayRange && (
        <div>
          <Form.Label>Rango de días en Full</Form.Label>
          <Row className="g-2">
            <Col xs={6}>
              <Form.Group>
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder="Día inicio"
                  min={0}
                  {...register("dayRangeFrom", {
                    required: "Requerido",
                    valueAsNumber: true,
                    min: { value: 0, message: "≥ 0" },
                  })}
                  isInvalid={!!errors.dayRangeFrom}
                />
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                  Día inicio (inclusive)
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.dayRangeFrom?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group>
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder="Sin límite"
                  min={0}
                  {...register("dayRangeTo", {
                    validate: (val) => {
                      if (val === "" || val === undefined) return true;
                      const num = Number(val);
                      if (isNaN(num)) return "Debe ser un número";
                      if (num <= dayRangeFrom) return `Debe ser > ${dayRangeFrom}`;
                      return true;
                    },
                  })}
                  isInvalid={!!errors.dayRangeTo}
                />
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                  Día fin (inclusive, vacío = sin límite)
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.dayRangeTo?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* Porcentaje de ajuste — solo si la regla tiene acción descuentos/api_bonos */}
      {hasPercentage && (
        <Form.Group>
          <Form.Label>Porcentaje de ajuste (%)</Form.Label>
          <Form.Control
            type="number"
            size="sm"
            step="0.01"
            min={0}
            max={100}
            placeholder="Ej: 3"
            {...register("percentage", {
              required: "Requerido",
              valueAsNumber: true,
              min: { value: 0, message: "≥ 0" },
              max: { value: 100, message: "≤ 100" },
            })}
            isInvalid={!!errors.percentage}
          />
          <Form.Text className="text-muted" style={{ fontSize: 11 }}>
            Se aplica sobre todas las acciones de descuento/bono de esta regla
          </Form.Text>
          <Form.Control.Feedback type="invalid">
            {errors.percentage?.message}
          </Form.Control.Feedback>
        </Form.Group>
      )}

      <Form.Group>
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0">Estado actual:</Form.Label>
          <span className={`badge-status ${watch("isActive") ? "active" : "inactive"}`}>
            {watch("isActive") ? "● Activa" : "○ Inactiva"}
          </span>
        </div>
        <Form.Text className="d-block text-muted mt-1" style={{ fontSize: 11 }}>
          Para cambiar el estado usá el botón Activar / Desactivar en la tabla.
        </Form.Text>
      </Form.Group>

    </StyledRulesModalForm>
  );
};
